import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Save, Building2, Phone, Mail, MapPin, Instagram, BarChart3, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Toaster } from '../../components/ui/sonner';

const AdminSettings = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState({
    company_name: '',
    slogan: '',
    phone: '',
    whatsapp: '',
    email: '',
    address: '',
    instagram_luvcorp: '',
    instagram_salveobreiros: '',
    stat_condominiums: '',
    stat_years: '',
    stat_satisfaction: '',
    crea_number: '',
    engineer_name: '',
    about_text: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Configurações salvas com sucesso!');
      } else {
        throw new Error('Erro ao salvar');
      }
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#15448A]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Configurações do Site</h1>
            <p className="text-gray-500">Edite as informações gerais do site</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[#15448A]">
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Salvando...
              </>
            ) : (
              <>
                <Save size={18} className="mr-2" />
                Salvar Alterações
              </>
            )}
          </Button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm text-amber-800 font-medium">Atenção</p>
            <p className="text-sm text-amber-700">As alterações feitas aqui serão refletidas em todo o site após salvar.</p>
          </div>
        </div>

        <Tabs defaultValue="company" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="company">Empresa</TabsTrigger>
            <TabsTrigger value="contact">Contato</TabsTrigger>
            <TabsTrigger value="stats">Estatísticas</TabsTrigger>
            <TabsTrigger value="about">Quem Somos</TabsTrigger>
          </TabsList>

          {/* Empresa */}
          <TabsContent value="company">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Building2 size={20} />
                  <span>Informações da Empresa</span>
                </CardTitle>
                <CardDescription>Dados principais da empresa exibidos no site</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nome da Empresa</Label>
                    <Input 
                      value={settings.company_name} 
                      onChange={(e) => handleChange('company_name', e.target.value)}
                      placeholder="LUVCORP Soluções Prediais"
                    />
                  </div>
                  <div>
                    <Label>Slogan</Label>
                    <Input 
                      value={settings.slogan} 
                      onChange={(e) => handleChange('slogan', e.target.value)}
                      placeholder="Revitalizamos o Seu Sonho"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Número CREA</Label>
                    <Input 
                      value={settings.crea_number} 
                      onChange={(e) => handleChange('crea_number', e.target.value)}
                      placeholder="2025201541"
                    />
                  </div>
                  <div>
                    <Label>Engenheiro Responsável</Label>
                    <Input 
                      value={settings.engineer_name} 
                      onChange={(e) => handleChange('engineer_name', e.target.value)}
                      placeholder="Engenheiro Matheus de Souza"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contato */}
          <TabsContent value="contact">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Phone size={20} />
                  <span>Informações de Contato</span>
                </CardTitle>
                <CardDescription>Dados de contato exibidos no site e usados nos formulários</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Telefone</Label>
                    <Input 
                      value={settings.phone} 
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="(21) 99999-9999"
                    />
                  </div>
                  <div>
                    <Label>WhatsApp (apenas números com DDI)</Label>
                    <Input 
                      value={settings.whatsapp} 
                      onChange={(e) => handleChange('whatsapp', e.target.value)}
                      placeholder="5521999999999"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>E-mail</Label>
                    <Input 
                      value={settings.email} 
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="contato@luvcorp.com.br"
                    />
                  </div>
                  <div>
                    <Label>Endereço</Label>
                    <Input 
                      value={settings.address} 
                      onChange={(e) => handleChange('address', e.target.value)}
                      placeholder="Rio de Janeiro, RJ"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Instagram LUVCORP (sem @)</Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">@</span>
                      <Input 
                        value={settings.instagram_luvcorp} 
                        onChange={(e) => handleChange('instagram_luvcorp', e.target.value)}
                        placeholder="luvcorp_"
                        className="rounded-l-none"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Instagram Salve Obreiros (sem @)</Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">@</span>
                      <Input 
                        value={settings.instagram_salveobreiros} 
                        onChange={(e) => handleChange('instagram_salveobreiros', e.target.value)}
                        placeholder="salveobreiros"
                        className="rounded-l-none"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Estatísticas */}
          <TabsContent value="stats">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 size={20} />
                  <span>Estatísticas do Site</span>
                </CardTitle>
                <CardDescription>Números exibidos na página inicial</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Condomínios Atendidos</Label>
                    <Input 
                      value={settings.stat_condominiums} 
                      onChange={(e) => handleChange('stat_condominiums', e.target.value)}
                      placeholder="45+"
                    />
                    <p className="text-xs text-gray-500 mt-1">Ex: 45+, 50, 100+</p>
                  </div>
                  <div>
                    <Label>Anos de Experiência</Label>
                    <Input 
                      value={settings.stat_years} 
                      onChange={(e) => handleChange('stat_years', e.target.value)}
                      placeholder="5+"
                    />
                    <p className="text-xs text-gray-500 mt-1">Ex: 5+, 10, 15+</p>
                  </div>
                  <div>
                    <Label>Satisfação dos Clientes</Label>
                    <Input 
                      value={settings.stat_satisfaction} 
                      onChange={(e) => handleChange('stat_satisfaction', e.target.value)}
                      placeholder="95%"
                    />
                    <p className="text-xs text-gray-500 mt-1">Ex: 95%, 98%, 100%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quem Somos */}
          <TabsContent value="about">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText size={20} />
                  <span>Página Quem Somos</span>
                </CardTitle>
                <CardDescription>Texto principal da página "Quem Somos"</CardDescription>
              </CardHeader>
              <CardContent>
                <div>
                  <Label>Texto Sobre a Empresa</Label>
                  <Textarea 
                    value={settings.about_text} 
                    onChange={(e) => handleChange('about_text', e.target.value)}
                    rows={12}
                    placeholder="Descreva a história, missão e valores da empresa..."
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Dica: Separe parágrafos com uma linha em branco para melhor formatação.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
